import type { DumbStorageSerializer } from "@/composables/use-dumb-storage";
import * as z from "zod/v4";

export const cookieConsentSchema = z.object({
  version: z.literal(2),
  marketing: z.boolean(),
  personalization: z.boolean(),
  analytics: z.boolean(),
  consented_at: z.number(),
});

export type CookieConsent = z.infer<typeof cookieConsentSchema>;

// 12 months in milliseconds
const CONSENT_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

export function createDenyAllConsent(): CookieConsent {
  return {
    version: 2,
    marketing: false,
    personalization: false,
    analytics: false,
    consented_at: Date.now(),
  };
}

export function createAcceptAllConsent(): CookieConsent {
  return {
    version: 2,
    marketing: true,
    personalization: true,
    analytics: true,
    consented_at: Date.now(),
  };
}

export const cookieConsentSerializer: DumbStorageSerializer<CookieConsent | null> =
  {
    write: (str) => {
      return JSON.stringify(str);
    },
    read: (str) => {
      if (!str) return null;

      try {
        const json = JSON.parse(str);

        // Version 1 consent is treated as expired
        if (json.version === 1) {
          return null;
        }

        // Parse with version 2 schema
        const parsed = cookieConsentSchema.parse(json);

        // Check if consent has expired (older than 12 months)
        const now = Date.now();
        const age = now - parsed.consented_at;

        if (age > CONSENT_EXPIRY_MS) {
          return null;
        }

        return parsed;
      } catch (error) {
        console.error("Failed to parse cookie consent", error);
        return null;
      }
    },
  };
